import * as React from "react";
import { useQuery } from "@apollo/react-hooks";
import formatDate from "dateformat";
import { COVID_CASES_BY_COUNTRY } from "../../query";
import DailyCaseCard from "../daily-case-card";
import {
  formatNumber,
  formatNumberDisplay,
} from "../../../../../../utils/format-number";
import {
  CovidCaseByCountry,
  CovidCaseByCountryVariables,
} from "../../../../../../apollo-types/CovidCaseByCountry";
import LoadingState from "../../../../../ui-library/loading-state/loading-state";
import CountrySnapshotCard from "../country-snapshot-card";
import BenchmarkCountrySuggest from "../../country-suggest/benchmark-country-suggest";
import { AskAboutContext } from "../../../../../../site-context/ask-about-context";
import { useLatestCovidCases } from "../../../../../../hooks/static-queries/use-latest-covid-cases";
import { FAKE_LAST_DAY } from "../../../../../../constants/fake-last-day-data";
import CountryInfectionRate from "../../infection-rate/country-infection-rate";
import ConfirmedCasesContent from "./global-card-content/confirmed-cases";
import DeathCasesContent from "./global-card-content/death-cases";
import FatalityRateContent from "./global-card-content/fatality-rate";
import ActiveCasesContent from "./global-card-content/active-cases";
import RecoveredCasesContent from "./global-card-content/recovered-cases";
import TestsPerformedContent from "./global-card-content/tests-performed";

interface DailyCaseByGlobalProps {
  intentName: string;
}

const DailyCaseByGlobal: React.FC<DailyCaseByGlobalProps> = ({
  intentName,
}) => {
  const {
    store: {
      userCountry: [{ benchmarkCountry }],
    },
  } = React.useContext(AskAboutContext);

  const { data, loading } = useQuery<
    CovidCaseByCountry,
    CovidCaseByCountryVariables
  >(COVID_CASES_BY_COUNTRY, {
    variables: { country: "World" },
  });

  const { data: benchmarkData } = useQuery<
    CovidCaseByCountry,
    CovidCaseByCountryVariables
  >(COVID_CASES_BY_COUNTRY, {
    variables: { country: benchmarkCountry?.country || "" },
    skip: !benchmarkCountry?.country,
  });

  const latestCovidCases = useLatestCovidCases();

  const covidData = data?.covidCaseOwidByCountry?.data;
  const covidBenchmarkData = benchmarkData?.covidCaseOwidByCountry?.data;

  const lastDayValue = data?.covidCaseOwidByCountry?.latest || FAKE_LAST_DAY;

  const headingDetail = `Last updated ${formatDate(
    lastDayValue.date,
    "dd mmm yyyy"
  )}`;

  const testsUnits = " tests conducted";

  return (
    <>
      {!loading ? (
        covidData && (
          <CountrySnapshotCard
            countryName="globally"
            lastDayValue={lastDayValue}
            covidData={covidData}
            headingDetail={headingDetail}
          />
        )
      ) : (
        <LoadingState height={290} />
      )}

      <BenchmarkCountrySuggest />

      <DailyCaseCard
        id="infectionRate"
        intentName={intentName}
        answerIndex="Confirmed cases"
        heading="Infection rate over the last 30 days"
        headingDetail={headingDetail}
        subHeading="To control the outbreak it needs to be kept below 1.0"
      >
        <CountryInfectionRate
          countryName="Worldwide"
          data={covidData}
          benchmarkCountryName={benchmarkCountry?.countryName}
          benchmarkData={covidBenchmarkData}
          loading={loading}
        />
      </DailyCaseCard>

      <DailyCaseCard
        id="dailyConfirm"
        intentName={intentName}
        answerIndex="Confirmed cases"
        heading={`${formatNumber(
          lastDayValue.totalCases || 0
        )} total cases globally`}
        headingDetail={headingDetail}
      >
        <ConfirmedCasesContent
          covidData={covidData}
          latestCovidCases={latestCovidCases}
        />
      </DailyCaseCard>

      <DailyCaseCard
        id="dailyActive"
        intentName={intentName}
        answerIndex="Active cases"
        heading={`${formatNumber(
          lastDayValue.totalActive || 0
        )} active cases globally`}
        headingDetail={headingDetail}
      >
        <ActiveCasesContent
          covidData={covidData}
          latestCovidCases={latestCovidCases}
        />
      </DailyCaseCard>

      <DailyCaseCard
        id="dailyRecovered"
        intentName={intentName}
        answerIndex="Recovered cases"
        heading={`${formatNumber(
          lastDayValue.totalRecovered || 0
        )} recovered cases globally`}
        headingDetail={headingDetail}
      >
        <RecoveredCasesContent
          covidData={covidData}
          latestCovidCases={latestCovidCases}
        />
      </DailyCaseCard>

      <DailyCaseCard
        id="dailyTested"
        intentName={intentName}
        answerIndex="Number of tests"
        heading={`${formatNumber(
          lastDayValue.totalTests || 0
        )}${testsUnits} globally`}
        headingDetail={headingDetail}
      >
        <TestsPerformedContent
          covidData={covidData}
          latestCovidCases={latestCovidCases}
        />
      </DailyCaseCard>

      <DailyCaseCard
        id="dailyDeath"
        intentName={intentName}
        answerIndex="Death cases"
        heading={`${formatNumber(
          lastDayValue.totalDeaths || 0
        )} death cases globally`}
        headingDetail={headingDetail}
      >
        <DeathCasesContent
          covidData={covidData}
          latestCovidCases={latestCovidCases}
        />
      </DailyCaseCard>

      <DailyCaseCard
        id="dailyFatalityRate"
        intentName={intentName}
        answerIndex="Fatality rate"
        heading={`${formatNumberDisplay(
          lastDayValue.fatalityRate || 0,
          true
        )}% case fatality rate globally`}
        headingDetail={headingDetail}
      >
        <FatalityRateContent
          covidData={covidData}
          latestCovidCases={latestCovidCases}
        />
      </DailyCaseCard>
    </>
  );
};

export default DailyCaseByGlobal;
